const CONTEXT_TTL_MS = 30 * 60 * 1000

export function createCompanyAreaContext({ storageKey, attributePrefix }) {
  function getSessionStorage() {
    try {
      return window.sessionStorage
    } catch (error) {
      return null
    }
  }

  function readContext(now = Date.now()) {
    const storage = getSessionStorage()
    if (!storage) return null

    let value
    try {
      value = JSON.parse(storage.getItem(storageKey) || '{}')
    } catch (error) {
      storage.removeItem(storageKey)
      return null
    }
    if (!value || typeof value !== 'object') return null

    const areaName = typeof value.areaName === 'string' ? value.areaName.trim() : ''
    const companyPath = typeof value.companyPath === 'string' ? value.companyPath : ''
    const prefectureName = typeof value.prefectureName === 'string' ? value.prefectureName.trim() : ''
    const cityName = typeof value.cityName === 'string' ? value.cityName.trim() : ''
    const storedAt = Number(value.storedAt)
    if (!areaName || !companyPath || !Number.isFinite(storedAt)) return null

    if (now - storedAt > CONTEXT_TTL_MS) {
      storage.removeItem(storageKey)
      return null
    }

    return { areaName, companyPath, prefectureName, cityName }
  }

  function storeContext(link, now = Date.now()) {
    const areaName = (link.getAttribute(`data-${attributePrefix}-area-context`) || '').trim()
    const prefectureName = (link.getAttribute(`data-${attributePrefix}-area-prefecture`) || '').trim()
    const cityName = (link.getAttribute(`data-${attributePrefix}-area-city`) || '').trim()
    const storage = getSessionStorage()
    if (!storage || !areaName) return

    try {
      const companyPath = new URL(link.href, window.location.href).pathname
      storage.setItem(storageKey, JSON.stringify({ areaName, prefectureName, cityName, companyPath, storedAt: now }))
    } catch (error) {
    }
  }

  function initArchiveAreaContext(root = document) {
    root.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest(`a[data-${attributePrefix}-area-context]`) : null
      if (!(link instanceof HTMLAnchorElement)) return

      storeContext(link)
    })
  }

  function applyDetailAreaContext(root = document, now = Date.now()) {
    const context = readContext(now)
    if (!context || context.companyPath !== window.location.pathname) return false

    const serviceAreaApplied = applyServiceAreaContext(root, context)
    const locationApplied = applyLocationContext(root, context)
    return serviceAreaApplied || locationApplied
  }

  function applyServiceAreaContext(root, context) {
    const container = root.querySelector(`[data-${attributePrefix}-detail-service-area]`)
    const textElement = root.querySelector(`[data-${attributePrefix}-detail-service-area-text]`)
    if (!(container instanceof HTMLElement) || !(textElement instanceof HTMLElement)) return false
    if (container.getAttribute(`data-${attributePrefix}-detail-area-context-applied`) === context.areaName) return true

    const currentText = (textElement.textContent || '').trim()
    if (currentText === '') return false

    const label = document.createElement('strong')
    label.className = 'text-hapisumu-orange'
    label.textContent = context.areaName
    container.insertBefore(label, textElement)

    if (currentText.startsWith(context.areaName)) {
      textElement.textContent = currentText.slice(context.areaName.length)
    } else {
      container.insertBefore(document.createTextNode('・'), textElement)
    }
    container.setAttribute(`data-${attributePrefix}-detail-area-context-applied`, context.areaName)

    return true
  }

  function officeAddressesFromElement(element) {
    try {
      const addresses = JSON.parse(element.getAttribute(`data-${attributePrefix}-detail-office-addresses`) || '[]')
      return Array.isArray(addresses) ? addresses.filter((address) => typeof address === 'string' && address.trim() !== '') : []
    } catch (error) {
      return []
    }
  }

  function findOfficeAddress(addresses, keyword) {
    return keyword ? addresses.find((address) => address.includes(keyword)) || '' : ''
  }

  function applyLocationContext(root, context) {
    const container = root.querySelector(`[data-${attributePrefix}-detail-location]`)
    const textElement = root.querySelector(`[data-${attributePrefix}-detail-location-text]`)
    if (!(container instanceof HTMLElement) || !(textElement instanceof HTMLElement)) return false

    const addresses = officeAddressesFromElement(container)
    const address = findOfficeAddress(addresses, context.cityName) || findOfficeAddress(addresses, context.prefectureName)
    if (address === '') return false

    textElement.textContent = address
    return true
  }

  return { initArchiveAreaContext, applyDetailAreaContext }
}
